"use client"

import { usePersona } from "@opensea/navigation"
import {
  COLLECTION_SETTINGS_MARKETPLACE_LOGOS_COOKIE_NAME,
  COLLECTION_SETTINGS_RARITY_COOKIE_NAME,
  getDefaultCollectionSettingsMarketplaceLogos,
  getDefaultCollectionSettingsRarity,
  parseShowMarketplaceLogos,
  parseShowRarity,
} from "@opensea/navigation/collection-settings"
import { useCookie, useOnChange } from "@opensea/ui-kit/hooks"
import { createContext } from "use-context-selector"

type CollectionSettingsContextValue = {
  showMarketplaceLogos: boolean
  setShowMarketplaceLogos: (value: boolean) => void
  showRarity: boolean
  setShowRarity: (value: boolean) => void
  isRarityDisabled: boolean
}

export const CollectionSettingsContext =
  createContext<CollectionSettingsContextValue>({
    showMarketplaceLogos: false,
    setShowMarketplaceLogos: () => undefined,
    showRarity: false,
    setShowRarity: () => undefined,
    isRarityDisabled: false,
  })

type Props = {
  children: React.ReactNode
  showMarketplaceLogos?: boolean
  showRarity?: boolean
  isRarityDisabled?: boolean
}

export function CollectionSettingsProvider({
  children,
  showMarketplaceLogos: initialShowMarketplaceLogos,
  showRarity: initialShowRarity,
  isRarityDisabled = false,
}: Props) {
  const { persona } = usePersona()
  const [
    collectionSettingsMarketplaceLogos,
    setCollectionSettingsMarketplaceLogosCookie,
  ] = useCookie(COLLECTION_SETTINGS_MARKETPLACE_LOGOS_COOKIE_NAME)
  const [collectionSettingsRarity, setCollectionSettingsRarityCookie] =
    useCookie(COLLECTION_SETTINGS_RARITY_COOKIE_NAME)

  const resolvedShowMarketplaceLogos =
    (collectionSettingsMarketplaceLogos
      ? parseShowMarketplaceLogos(collectionSettingsMarketplaceLogos)
      : initialShowMarketplaceLogos) ??
    getDefaultCollectionSettingsMarketplaceLogos()

  const resolvedShowRarity =
    !isRarityDisabled &&
    ((collectionSettingsRarity
      ? parseShowRarity(collectionSettingsRarity)
      : initialShowRarity) ??
      getDefaultCollectionSettingsRarity())

  // Reset cookie values when persona changes
  useOnChange(persona, () => {
    setCollectionSettingsMarketplaceLogosCookie(
      getDefaultCollectionSettingsMarketplaceLogos().toString(),
    )
    setCollectionSettingsRarityCookie(
      getDefaultCollectionSettingsRarity().toString(),
    )
  })

  const value = {
    showMarketplaceLogos: resolvedShowMarketplaceLogos,
    setShowMarketplaceLogos: (showLogos: boolean) =>
      setCollectionSettingsMarketplaceLogosCookie(showLogos.toString()),
    showRarity: resolvedShowRarity,
    setShowRarity: (showRarity: boolean) =>
      setCollectionSettingsRarityCookie(showRarity.toString()),
    isRarityDisabled,
  }

  return (
    <CollectionSettingsContext.Provider value={value}>
      {children}
    </CollectionSettingsContext.Provider>
  )
}
